// Auto-name builder for FREIGHT recaps (Aug 2026 — task card
// freight-recap-auto-naming).
//
//   freight  counterparty (owners, else charterer) · qty+unit · product
//            · laycan FULL span
//
// Trade recaps (cargo and bunkers) are not named here any more: the server
// is the one home for the name of a deal, recap, delivery and budget
// (backend/offer/deal_names.py, IMT auto-naming, Chris 2026-10-19) and every
// trade-recap answer carries its ``display_name``. Freight recaps have no
// buyer or seller, so they keep this name.
//
// The name is derived at render and never persisted: a stored
// ``recap_display_name`` is only ever a trader's own custom text, and
// whenever it is present it wins verbatim.
import { formatAmount } from './amountFormat';

export const RECAP_NAME_PROFILES = {
  FREIGHT: 'freight',
};

// Freight joins on a middot — it still parses as separate facts in a
// narrow list cell.
const PART_SEPARATOR = ' · ';

const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

// ``laycan`` is free text on both models ("Laycan period in format
// 'YYYY-MM-DD to YYYY-MM-DD'"), but it can also hold a single date, a
// dash-separated range, or whatever a trader typed straight into the
// picker. Two dashed shapes are parsed — ISO (what DateRangePicker
// writes) and DD-MM-YYYY (what it displays and accepts by hand) — and
// nothing else: slash dates are genuinely ambiguous between DD/MM and
// MM/DD, and a wrong laycan in a recap name is worse than an
// unformatted one, so anything unrecognised is echoed back verbatim.
const LAYCAN_RANGE_SEPARATOR = /\s+(?:to|-|–|—)\s+/i;

const parseLaycanDate = (token) => {
  const raw = text(token);
  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const dmy = iso ? null : raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (!iso && !dmy) return null;
  const year = Number(iso ? iso[1] : dmy[3]);
  const month = Number(iso ? iso[2] : dmy[2]);
  const day = Number(iso ? iso[3] : dmy[1]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
};

const formatSingleDate = ({ year, month, day }) => (
  `${day} ${MONTH_ABBREVIATIONS[month - 1]} ${year}`
);

/** Full laycan span, e.g. "15–25 Sep 2026". Never emits a half-open
 *  range: when only one end parses, only that end prints. */
export const formatLaycanSpan = (laycan) => {
  const raw = text(laycan);
  if (!raw) return '';
  const [startToken, endToken] = raw.split(LAYCAN_RANGE_SEPARATOR);
  const start = parseLaycanDate(startToken);
  const end = parseLaycanDate(endToken);
  if (!start && !end) return raw;
  if (!end) return formatSingleDate(start);
  if (!start) return formatSingleDate(end);
  if (start.year === end.year && start.month === end.month) {
    if (start.day === end.day) return formatSingleDate(start);
    return `${start.day}–${end.day} ${MONTH_ABBREVIATIONS[end.month - 1]} ${end.year}`;
  }
  if (start.year === end.year) {
    return `${start.day} ${MONTH_ABBREVIATIONS[start.month - 1]} – ${formatSingleDate(end)}`;
  }
  return `${formatSingleDate(start)} – ${formatSingleDate(end)}`;
};

// Quantity with thousands separators plus the recap's own unit. Falls back
// to the raw text when the column holds something non-numeric (these are
// CharFields — traders type "abt 50000") so a typed quantity is never
// silently dropped.
const quantityLabel = (value, unit) => {
  const raw = text(value);
  if (!raw) return '';
  return `${formatAmount(raw) || raw} ${text(unit) || 'MT'}`;
};

const buildFreightName = (recap) => {
  const parts = [];
  // Counterparty = Owners. HMS is normally the charterer, so the owner is
  // the other side of the deal; charterer is the fallback for the rare
  // recap where HMS owns the tonnage.
  const counterparty = text(recap.owners) || text(recap.charterer);
  if (counterparty) parts.push(counterparty);
  const quantity = quantityLabel(recap.quantity_mt, recap.quantity_unit);
  if (quantity) parts.push(quantity);
  const product = text(recap.product);
  if (product) parts.push(product);
  const laycan = formatLaycanSpan(recap.laycan);
  if (laycan) parts.push(laycan);
  return parts.join(PART_SEPARATOR);
};

/**
 * The name a freight recap shows anywhere it is listed or opened.
 *
 * @param {Object} recap - Freight recap payload or form-fields object. Read-only.
 * @param {Object} [options]
 * @param {string} [options.profile]    - RECAP_NAME_PROFILES.FREIGHT (the only
 *                                        profile; trade recaps are named by
 *                                        the server).
 * @param {string} [options.customName] - The trader's typed name when the
 *                                        caller holds it in local state.
 *                                        Defaults to recap_display_name.
 * @returns {string} Never blank — falls back to "Freight Recap".
 */
export const buildRecapDisplayName = (recap, options = {}) => {
  const source = recap || {};
  const { customName } = options;

  // A trader-typed name always wins and is never regenerated over.
  const custom = text(customName !== undefined ? customName : source.recap_display_name);
  if (custom) return custom;

  return buildFreightName(source) || 'Freight Recap';
};
